import React from "react";
import { Shield, Eye, Server, Lock } from "lucide-react";
import { useLanguage } from "@/i18n";

const privacyIcons = [Shield, Eye, Server, Lock];

const PrivacySection = () => {
  const { t } = useLanguage();
  const privacyFeatures = t('privacy.features');

  return (
    <section id="privacy" className="py-20 sm:py-28 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left content */}
          <div className="space-y-8">
            {/* Section header */}
            <div className="space-y-4">
              <span className="text-primary text-sm font-medium uppercase tracking-widest">
                {t('privacy.label')}
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground">
                {t('privacy.title')}{" "}
                <span className="text-gradient">{t('privacy.titleHighlight')}</span>
              </h2>
              <p className="text-muted-foreground text-lg">
                {t('privacy.description')}
              </p>
            </div>

            {/* Privacy features */}
            <div className="grid sm:grid-cols-2 gap-4">
              {privacyFeatures.map((feature, index) => {
                const Icon = privacyIcons[index];
                return (
                  <div
                    key={index}
                    className="p-5 rounded-2xl bg-card/50 border border-border/50 hover:border-primary/30 transition-colors duration-300"
                  >
                    <Icon className="w-8 h-8 text-primary mb-3" />
                    <h3 className="font-semibold text-foreground mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right visual */}
          <div className="relative">
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-accent/10 to-secondary/20 blur-3xl" />
            
            {/* Shield illustration */}
            <div className="relative glass-card rounded-3xl p-8 sm:p-12 text-center">
              <div className="relative inline-block">
                <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-3xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mx-auto mb-6">
                  <Shield className="w-16 h-16 sm:w-20 sm:h-20 text-primary" />
                </div>
                {/* Orbiting elements */}
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-accent rounded-lg flex items-center justify-center animate-float">
                  <Lock className="w-4 h-4 text-accent-foreground" />
                </div>
                <div className="absolute -bottom-2 -left-2 w-8 h-8 bg-secondary rounded-lg flex items-center justify-center animate-float" style={{ animationDelay: '1s' }}>
                  <Eye className="w-4 h-4 text-secondary-foreground" />
                </div>
              </div>
              
              <h3 className="text-xl font-semibold text-foreground mb-2">
                {t('privacy.shieldTitle')}
              </h3>
              <p className="text-muted-foreground text-sm max-w-sm mx-auto">
                {t('privacy.shieldDescription')}
              </p>
              
              {/* Trust indicators */}
              <div className="flex items-center justify-center gap-6 mt-6 pt-6 border-t border-border/50">
                <div className="text-center">
                  <p className="text-2xl font-bold text-foreground">0</p>
                  <p className="text-xs text-muted-foreground">{t('privacy.stats.dataCollected')}</p>
                </div>
                <div className="w-px h-8 bg-border" />
                <div className="text-center">
                  <p className="text-2xl font-bold text-foreground">100%</p>
                  <p className="text-xs text-muted-foreground">{t('privacy.stats.localStorage')}</p>
                </div>
                <div className="w-px h-8 bg-border" />
                <div className="text-center">
                  <p className="text-2xl font-bold text-foreground">MIT</p>
                  <p className="text-xs text-muted-foreground">{t('privacy.stats.licensed')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PrivacySection;
